import React, { useCallback, useMemo } from 'react';
import Typography from '../../../StyledTypography';
import IDSField from '../inputs/IDSField';
import HeadingField from '../fields/HeadingField';
import ParagraphField from '../fields/ParagraphField';
import TabsField from '../fields/TabsField';
import { isFieldVisible } from '../../../../utils/formCrafter/visibilityUtils';
import { lookupStagedFiles } from '../../../../utils/stagedFiles';
import { getRepeaterInitialStagedFiles } from '../../../../utils/formCrafter/repeaterFileUtils';
import { FIELD_TYPES } from '../../../../constants/formCrafter.constant';
import styles from './IDSSubformRenderer.module.css';

/**
 * IDSSubformRenderer - Organism that renders a single subform using IDS components
 *
 * Iterates through normalized schema fields and renders each using IdsField molecule.
 * Handles field visibility conditions and error display.
 *
 * @param {Object} schema - Normalized FormCrafter schema with fields array
 * @param {Object} values - Current field values { fieldId: value }
 * @param {Function} onChange - Callback when any field value changes (fieldId, value)
 * @param {Function} onBlur - Callback when any field loses focus (fieldId)
 * @param {Object} errors - Error messages by field ID { fieldId: errorMessage }
 * @param {Object} touched - Touched state by field ID { fieldId: boolean }
 * @param {boolean} disabled - Whether all fields are disabled
 * @param {boolean} showTitle - Whether to show schema title (default: false)
 * @param {Function} getExistingFiles - Function to get existing files for a field (fieldId) => files[]
 * @param {Function} onServerFileDelete - Callback when a server file is deleted (fileId)
 */
const IDSSubformRenderer = ({
  schema,
  values = {},
  onChange,
  onBlur,
  errors = {},
  touched = {},
  disabled = false,
  showTitle = false,
  getExistingFiles,
  onServerFileDelete,
  // Props for eager file upload
  formId,
  subformId,
  onStagedFileUpload,
  onStagedFileDelete,
  eagerUploadEnabled = false,
  // Draft staged files (pre-loaded from draft)
  draftStagedFiles = {},
}) => {
  const { title, description, fields = [] } = schema || {};

  const getRepeaterInitialStagedFilesCb = useCallback(
    (repeaterField) =>
      getRepeaterInitialStagedFiles(
        repeaterField,
        draftStagedFiles,
        formId,
        subformId
      ),
    [draftStagedFiles, formId, subformId]
  );

  // Get IDs of fields that belong to tabs (so we don't render them twice)
  const tabFieldIds = useMemo(() => {
    const ids = new Set();
    fields.forEach((field) => {
      if (field.type === 'tabs' && field.tabContent) {
        Object.values(field.tabContent).forEach((tabFields) => {
          if (Array.isArray(tabFields)) {
            tabFields.forEach((tf) => {
              ids.add(tf.value || tf.id);
            });
          }
        });
      }
    });
    return ids;
  }, [fields]);

  // Filter visible fields (exclude fields inside tabs - they render inside TabsField)
  const visibleFields = useMemo(() => {
    const filtered = fields.filter((field) => {
      // Skip divider fields
      if (field.type === 'divider') {
        return false;
      }
      // Skip fields that belong to tabs (they're rendered inside TabsField)
      const isTabField = tabFieldIds.has(field.id);
      if (isTabField) {
        return false;
      }
      return isFieldVisible(field, values);
    });
    return filtered;
  }, [fields, values, tabFieldIds]);

  // Handle field change
  const handleFieldChange = useCallback(
    (fieldId) => (newValue) => {
      onChange(fieldId, newValue);
    },
    [onChange]
  );

  // Handle field blur
  const handleFieldBlur = useCallback(
    (fieldId) => () => {
      if (onBlur) {
        onBlur(fieldId);
      }
    },
    [onBlur]
  );

  if (!fields || fields.length === 0) {
    return null;
  }

  return (
    <div className={styles.subformContainer}>
      {showTitle && title && (
        <Typography variant="title2" className={styles.title}>
          {title}
        </Typography>
      )}
      {showTitle && description && (
        <Typography variant="bodySmall" className={styles.description}>
          {description}
        </Typography>
      )}

      <div className={styles.fieldsContainer}>
        {visibleFields.map((field) => {
          // Handle decorative/display fields using atoms
          if (field.type === 'heading') {
            return (
              <HeadingField
                key={field.id}
                label={field.label}
                tag={field.headingTag}
                columnWidth={field.columnWidth}
              />
            );
          }
          if (field.type === 'paragraph') {
            return (
              <ParagraphField
                key={field.id}
                content={field.content}
                label={field.label}
                tag={field.paragraphType}
                columnWidth={field.columnWidth}
              />
            );
          }
          if (field.type === 'divider') {
            return <hr key={field.id} className={styles.divider} />;
          }
          if (field.type === 'tabs') {
            return (
              <TabsField
                key={field.id}
                field={field}
                allFields={fields}
                values={values}
                onChange={onChange}
                onBlur={onBlur}
                errors={errors}
                touched={touched}
                disabled={disabled}
                // The tabs field might contain file fields.
                getExistingFiles={getExistingFiles}
                onServerFileDelete={onServerFileDelete}
                formId={formId}
                subformId={subformId}
                onStagedFileUpload={onStagedFileUpload}
                onStagedFileDelete={onStagedFileDelete}
                eagerUploadEnabled={eagerUploadEnabled}
                draftStagedFiles={draftStagedFiles}
              />
            );
          }

          // Render editable field using IdsField molecule
          const existingFiles = getExistingFiles
            ? getExistingFiles(field.id)
            : [];

          /**
           * Get initial staged files for this field from draft.
           * Uses centralized lookupStagedFiles utility that handles both key formats:
           * - Full key: "formId-subformId-fieldId" (current format)
           * - Legacy key: "subformId-fieldId" (backwards compatibility)
           *
           * This is used when:
           * - Loading a draft for editing (new request or send-back)
           * - Displaying files uploaded during current session
           */
          // Check for repeater type (case-insensitive for compatibility)
          const isRepeaterField =
            field.type === FIELD_TYPES.REPEATER ||
            field.type?.toLowerCase() === 'repeater' ||
            Array.isArray(field.fields);

          const initialStagedFiles = isRepeaterField
            ? getRepeaterInitialStagedFilesCb(field)
            : lookupStagedFiles(draftStagedFiles, formId, subformId, field.id);
          return (
            <IDSField
              key={field.id}
              field={field}
              value={values[field.id]}
              onChange={handleFieldChange(field.id)}
              onBlur={handleFieldBlur(field.id)}
              error={errors[field.id]}
              disabled={disabled}
              touched={!!touched[field.id]}
              allErrors={errors}
              allTouched={touched}
              existingFiles={existingFiles}
              onServerFileDelete={onServerFileDelete}
              // Eager upload props
              formId={formId}
              subformId={subformId}
              onStagedFileUpload={onStagedFileUpload}
              onStagedFileDelete={onStagedFileDelete}
              eagerUploadEnabled={eagerUploadEnabled}
              initialStagedFiles={initialStagedFiles}
            />
          );
        })}
      </div>
    </div>
  );
};

export default IDSSubformRenderer;
